'use client'
import { GifPanelContext } from '@/app/components/detail/gif-panel-context-manager'
import { buildAuthHref } from '@/components/auth/urls'
import { FlashMessageContext } from '@/context/flash-message'
import { IGif, IUser } from '@giphy/js-types'
import { getContentOfGif, getCreatorOfGif, setGADataLayer } from 'analytics'
import download from 'downloadjs'
import { usePathname, useRouter } from 'next/navigation'
import { ReactNode, useContext, useRef } from 'react'
import sanitize from 'sanitize-filename'
import { timeoutFetch } from 'utils/src/api/fetch'
import { ShareUrlRenditions } from 'utils/src/media/sharing'

type Props = {
    className?: string
    gif: IGif
    children: ReactNode
}

export const DownloadToggle = ({ gif, className = '', children, user }: Props & { user?: IUser }) => {
    const pathname = usePathname()
    const router = useRouter()
    const { setPanel } = useContext(GifPanelContext)
    return (
        <div
            className={className}
            onClick={async () => {
                if (!user) {
                    router.push(buildAuthHref('/login', pathname))
                } else {
                    const url = gif.images.original.url
                    const imageUrl = url.replace('media.giphy.com', 'i.giphy.com')
                    const result = await timeoutFetch({ url: imageUrl, timeout: 10000 })
                    const blob = await result.blob()
                    // http://danml.com/download.html
                    download(blob, sanitize(`${gif.title}.gif`))
                    setPanel('downloaded')
                    setGADataLayer({
                        event: 'file_download',
                        options: {
                            content: getContentOfGif(gif, {
                                rendition: ShareUrlRenditions.Original,
                                format: gif.images.original.url,
                            }),
                            creator: getCreatorOfGif(gif),
                        },
                    })
                }
            }}
        >
            {children}
        </div>
    )
}

const getShareFile = async (gif: IGif) => {
    const imageUrl = gif.images.original.url
    const result = await timeoutFetch({ url: imageUrl, timeout: 10000 })

    if (!result.ok) {
        throw new Error(`GIF file download returned ${result.status} ${result.statusText}`)
    }

    const blob = await result.blob()
    const filename = sanitize(`${gif.title}.gif`) || 'giphy.gif'
    return new File([blob], filename, { type: blob.type || 'image/gif' })
}

type ShowMessage = (message: { message: ReactNode; type?: 'success' | 'error' }) => void

const isShareCancelled = (error: unknown) =>
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'

const shareGif = async (gif: IGif, showMessage: ShowMessage, file?: File) => {
    try {
        let socialChannel: 'share' | 'copy' | undefined

        if (file && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file] })
            socialChannel = 'share'
        } else if (navigator.share) {
            await navigator.share({ title: gif.title, url: gif.images.original.url })
            socialChannel = 'share'
        } else {
            if (!navigator.clipboard?.writeText) {
                throw new Error('GIF sharing and clipboard writing are not supported.')
            }

            socialChannel = 'copy'
            await navigator.clipboard.writeText(gif.images.original.url)
            showMessage({ message: 'GIF link copied to clipboard.', type: 'success' })
        }

        setGADataLayer({
            event: 'share_success',
            options: {
                social: {
                    socialChannel,
                },
                content: getContentOfGif(gif, {
                    rendition: ShareUrlRenditions.Original,
                    format: gif.images.original.url,
                }),
                creator: getCreatorOfGif(gif),
            },
        })
    } catch (error) {
        if (isShareCancelled(error)) {
            return
        }
        console.error('Unable to share or copy GIF.', error)
        showMessage({ message: 'Unable to share or copy this GIF.', type: 'error' })
    }
}

export const ShareButton = ({ className = '', children, gif }: Props) => {
    const { showMessage } = useContext(FlashMessageContext)
    const fileDownloadRef = useRef<{ file?: File; url: string } | null>(null)
    const isSharingRef = useRef(false)

    const startFileDownload = () => {
        const imageUrl = gif.images.original.url
        if (fileDownloadRef.current?.url === imageUrl) {
            return
        }

        const download: { file?: File; url: string } = { url: imageUrl }
        fileDownloadRef.current = download
        getShareFile(gif)
            .then((file) => {
                if (fileDownloadRef.current === download) {
                    download.file = file
                }
            })
            // URL sharing and clipboard remain available if the GIF download fails.
            .catch(() => {
                if (fileDownloadRef.current === download) {
                    fileDownloadRef.current = null
                }
            })
    }

    return (
        <div
            className={className}
            onFocus={startFileDownload}
            onPointerEnter={startFileDownload}
            onClick={async () => {
                if (isSharingRef.current) {
                    return
                }

                isSharingRef.current = true
                try {
                    const file =
                        fileDownloadRef.current?.url === gif.images.original.url
                            ? fileDownloadRef.current.file
                            : undefined
                    await shareGif(gif, showMessage, file)
                } finally {
                    isSharingRef.current = false
                }
            }}
        >
            {children}
        </div>
    )
}

export const CopyButton = ({ className = '', children, gif }: Props) => {
    const { setPanel } = useContext(GifPanelContext)
    return (
        <div
            className={className}
            onClick={() => {
                const imageUrl = gif.images.original.url
                navigator.clipboard.writeText(imageUrl)
                setPanel('copied')
                setGADataLayer({
                    event: 'share_success',
                    options: {
                        social: {
                            socialChannel: 'share',
                        },
                        content: getContentOfGif(gif, {
                            rendition: ShareUrlRenditions.Original,
                            format: gif.images.original.url,
                        }),
                        creator: getCreatorOfGif(gif),
                    },
                })
            }}
        >
            {children}
        </div>
    )
}
